"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import { Plus, Trash2, PenSquare, Eye, EyeOff, Globe, Lock, Monitor, Search, X, Settings2, Copy } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useTranslation } from "@/hooks/use-translation"
import { useWorkflow } from "@/context/workflow-context-simple"
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { useToast } from "@/hooks/use-toast"
import { useEntityType } from "@/contexts/entity-type-context"
import {
  isRequestsGroupEntity,
  useRequestsGroupState,
  type RequestsGroupEntityType,
} from "@/contexts/requests-group-state-context"
import { useViewManager, type AuthorizedClient } from "../view-manager-context"
import { SaveViewModal } from "@/components/view-selector"
import type { SavedView } from "@/types/saved-view"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type ViewCategory = 'private' | 'public' | 'default'
type ViewTypeFilter = 'all' | 'internal' | 'client'

interface ClientOrg {
  entityId: number
  displayName: string
  organizationCode: string
}

export function ViewsStep() {
  const { t } = useTranslation()
  const { state, dispatch, isDirty } = useViewManager()
  const { viewsResponse, configuration, refreshViews, deleteView, saveViewAs } = useWorkflow()
  const workflowApi = useWorkflowApi()
  const { fetchView, updateViewVisibility, searchClientOrganizations } = workflowApi
  const { toast } = useToast()
  // For visibility toggles on a Requests-group view, the BE fan-out
  // updates every sibling UserViewState. Bump the shared refresh signal
  // so other mounted sibling providers re-fetch and show the new
  // visibility immediately.
  const { entityType: contextEntityType } = useEntityType()
  const requestsGroupState = useRequestsGroupState()

  const [activeCategory, setActiveCategory] = useState<ViewCategory>('private')
  const [viewTypeFilter, setViewTypeFilter] = useState<ViewTypeFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<SavedView | null>(null)
  const [showClientFacingWarning, setShowClientFacingWarning] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  // Client org search state
  const [clientSearchQuery, setClientSearchQuery] = useState('')
  const [clientSearchResults, setClientSearchResults] = useState<ClientOrg[]>([])
  const [isSearchingClients, setIsSearchingClients] = useState(false)
  const [showClientResults, setShowClientResults] = useState(false)
  const clientSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastClientSearchRef = useRef('')
  // Stable refs to avoid re-running the useEffect on every render
  const searchClientOrgsRef = useRef(searchClientOrganizations)
  searchClientOrgsRef.current = searchClientOrganizations
  const draftClientsRef = useRef(state.draftAuthorizedClients)
  draftClientsRef.current = state.draftAuthorizedClients

  // Permissions
  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasModifyDefaultView = authorizedActions.includes("ModifyDefaultView")
  const hasManageClientViews = authorizedActions.includes("ManageClientViews")
  const hasModifyPublicViews = authorizedActions.includes("ModifyPublicViews")
  const hasManageViews = authorizedActions.includes("ManageViews")
  // Gate the per-view "Allow Excel export" toggle on the same authorized action
  // that gates the Export button itself — if the operator can't export, they
  // can't sensibly grant export, and showing the toggle would just confuse
  // them about why their saved value never takes effect downstream.
  const hasExportData = authorizedActions.includes("ExportData")

  // Client org search debounce — only re-runs when clientSearchQuery changes
  useEffect(() => {
    if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current)
    if (clientSearchQuery.length < 2) {
      setClientSearchResults([])
      setShowClientResults(false)
      return
    }
    clientSearchTimeoutRef.current = setTimeout(async () => {
      if (clientSearchQuery === lastClientSearchRef.current) return
      lastClientSearchRef.current = clientSearchQuery
      setIsSearchingClients(true)
      try {
        const results = await searchClientOrgsRef.current(clientSearchQuery)
        const selectedIds = draftClientsRef.current.map(c => c.entityId)
        setClientSearchResults(results.filter(r => !selectedIds.includes(r.entityId)))
        setShowClientResults(true)
      } catch {
        setClientSearchResults([])
      } finally {
        setIsSearchingClients(false)
      }
    }, 600)
    return () => { if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current) }
  }, [clientSearchQuery])

  // Apply filters
  const applyTypeFilter = (views: SavedView[]) => {
    if (viewTypeFilter === 'all') return views
    if (viewTypeFilter === 'client') return views.filter(v => v.isClientFacing)
    if (viewTypeFilter === 'internal') return views.filter(v => !v.isClientFacing)
    return views
  }

  const applySearchFilter = (views: SavedView[]) => {
    if (!searchTerm.trim()) return views
    const q = searchTerm.toLowerCase()
    return views.filter(v => v.title.toLowerCase().includes(q))
  }

  const filterViews = (views: SavedView[]) => applySearchFilter(applyTypeFilter(views))

  // Build default views list
  const buildDefaultViews = () => {
    const defaults: SavedView[] = []
    if (viewsResponse?.defaultView) defaults.push(viewsResponse.defaultView)
    if (viewsResponse?.clientDefaultView) defaults.push(viewsResponse.clientDefaultView)
    return filterViews(defaults)
  }

  // Categories
  const categories: { id: ViewCategory; label: string; views: SavedView[] }[] = [
    { id: 'private', label: t('vmCategoryPrivate' as any) || 'My Views', views: filterViews(viewsResponse?.userViews || []) },
    { id: 'public', label: t('public' as any) || 'Public', views: filterViews(viewsResponse?.publicViews || []) },
  ]
  if (hasModifyDefaultView) {
    categories.push({ id: 'default', label: t('vmCategoryDefault' as any) || 'Default', views: buildDefaultViews() })
  }

  const activeViews = categories.find(c => c.id === activeCategory)?.views || []

  /**
   * Load a view into the designer using only GET /views/{id} (lightweight).
   * Uses the existing `configuration` from workflow context for field metadata
   * (fieldsInUse, stage names, authorizedActions, etc.) — no need to re-fetch
   * the heavy configurations endpoint.
   *
   * Builds a config-like object by mapping the raw view's field IDs through
   * the context's field metadata.
   */
  const loadView = useCallback(async (view: SavedView) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const rawView = await fetchView(view.viewSettingsId)

      // Build a config object from the existing context configuration + raw view's field IDs
      const baseConfig = configuration
      if (!baseConfig) throw new Error('Configuration not loaded')

      // Build field lookup from all available fields
      // Start with fieldsInUse (has full metadata like isEnabledForClients, isPrivate, etc.)
      // then merge stage-level fields on top so stage-specific props are kept
      // but metadata from fieldsInUse is not lost.
      const allFieldsMap = new Map<string, any>()
      for (const f of (baseConfig.fieldsInUse || [])) {
        allFieldsMap.set(f.fieldConfigurationId, f)
      }
      for (const stage of (baseConfig.stageConfigurations || [])) {
        for (const f of (stage.fieldConfigurations || [])) {
          const existing = allFieldsMap.get(f.fieldConfigurationId)
          allFieldsMap.set(f.fieldConfigurationId, existing ? { ...existing, ...f } : f)
        }
      }

      // Build stage configurations using the raw view's field IDs
      // Note: raw view from API uses camelCase (stageConfigurations, fieldConfigurationIds, etc.)
      const rawStageConfigs = rawView?.stageConfigurations || rawView?.StageConfigurations || []
      const stageConfigurations = (baseConfig.stageConfigurations || []).map((baseStage: any) => {
        const stageId = baseStage.stageId || baseStage.stageCode
        const rawStage = rawStageConfigs.find(
          (rs: any) => (rs.stageConfigurationId || rs.StageConfigurationId) === stageId
        )
        const rawFieldIds: string[] = rawStage?.fieldConfigurationIds || rawStage?.FieldConfigurationIds || []

        return {
          ...baseStage,
          fieldConfigurations: rawFieldIds
            .map((id: string) => allFieldsMap.get(id))
            .filter(Boolean),
          sortColumn: rawStage?.sortColumn || rawStage?.SortColumn || null,
          sortDirection: rawStage?.sortDirection || rawStage?.SortDirection || null,
        }
      })

      const config = {
        ...baseConfig,
        stageConfigurations,
        viewSettingsId: view.viewSettingsId,
        savedGlobalFilters: rawView?.globalFilters || rawView?.GlobalFilters || [],
        savedStageFilters: rawView?.stageFilters || rawView?.StageFilters || [],
      }

      // Map authorized clients from raw view (handle both camelCase and PascalCase)
      const rawClients = rawView?.authorizedClientList || rawView?.AuthorizedClientList || []
      const authorizedClients = rawClients.length > 0
        ? rawClients.map((c: any) => ({ entityId: c.entityId ?? c.EntityId, name: c.name ?? c.Name }))
        : (view.authorizedClientList || []).map(c => ({ entityId: c.entityId, name: c.name }))

      dispatch({ type: 'LOAD_VIEW', payload: { view, config, authorizedClients } })
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to load view' })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [dispatch, fetchView, configuration])

  // Handlers
  const handleEditView = useCallback(async (view: SavedView) => {
    await loadView(view)
    dispatch({ type: 'SET_STEP', payload: 'columns' })
  }, [loadView, dispatch])

  // Select a view — check for unsaved changes first
  const handleSelectView = useCallback(async (view: SavedView) => {
    if (state.selectedView?.viewSettingsId === view.viewSettingsId) return
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Switching views will discard them. Continue?'
      )
      if (!confirmed) return
    }
    await loadView(view)
  }, [state.selectedView, isDirty, loadView])

  // Copy/duplicate a view
  const handleCopyView = useCallback(async (view: SavedView) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      // Fetch the raw view to get its full config
      const rawView = await fetchView(view.viewSettingsId)
      const rawStageConfigs = rawView?.stageConfigurations || rawView?.StageConfigurations || []
      const stageConfigs = rawStageConfigs.map((s: any) => ({
        stageConfigurationId: s.stageConfigurationId || s.StageConfigurationId,
        fieldConfigurationIds: s.fieldConfigurationIds || s.FieldConfigurationIds || [],
      }))

      // Create the copy with " - Copy" suffix. Force isDefault=false so copies of
      // default views land as regular private views, not new defaults.
      const result = await workflowApi.saveViewSettings({
        title: `${view.title} - Copy`,
        isPublic: false, // copies are always private initially
        isDefault: false,
        isTitleAndVisibilityUpdate: false,
        isUserViewStateUpdate: false,
        updateFilters: true,
        stageConfigurations: stageConfigs,
        stageFilters: rawView?.stageFilters || rawView?.StageFilters || [],
        globalFilters: rawView?.globalFilters || rawView?.GlobalFilters || [],
        visibleInApplications: [],
      })

      await refreshViews()
      toast({ variant: "success", title: "Success", description: `View copied as "${view.title} - Copy"` })
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to copy view' })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [dispatch, fetchView, workflowApi, refreshViews, toast])

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteView(deleteTarget.viewSettingsId)
      await refreshViews()
      if (state.selectedView?.viewSettingsId === deleteTarget.viewSettingsId) dispatch({ type: 'RESET' })
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to delete view' })
    }
    setDeleteTarget(null)
  }, [deleteTarget, deleteView, refreshViews, state.selectedView, dispatch])

  const handleToggleVisibility = useCallback(async (view: SavedView) => {
    try {
      await updateViewVisibility(view.viewSettingsId, view.isVisible === true)
      await refreshViews()
      // BE now fans the visibility toggle across every sibling in the
      // group — bump the shared refresh signal so other mounted sibling
      // providers (Client Requests / VMS Emails / etc.) re-fetch and
      // reflect the new visibility in their own dropdowns without a
      // remount.
      if (isRequestsGroupEntity(contextEntityType)) {
        requestsGroupState?.bumpRefreshSignal(contextEntityType as RequestsGroupEntityType)
      }
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to update visibility' })
    }
  }, [updateViewVisibility, refreshViews, dispatch, contextEntityType, requestsGroupState])

  // Create a new view — starts from scratch (no inherited columns)
  const handleCreateView = useCallback(async (title: string, isPublic: boolean, _saveFilters: boolean, isClientFacing: boolean, authorizedClients?: AuthorizedClient[]) => {
    // Close the modal immediately and go to columns step with loading state
    setIsCreateModalOpen(false)
    dispatch({ type: 'SET_STEP', payload: 'columns' })
    dispatch({ type: 'SET_LOADING', payload: true })

    try {
      const visibleInApplications = isClientFacing ? ['Client'] : []

      // Build stage configurations with stage IDs but empty field lists (start from scratch)
      // The backend requires stageConfigurations to be non-empty, so we pass the stage IDs
      // with no fields — when loaded via configurations endpoint, the backend will use
      // the default stage field configurations as fallback
      const stages = configuration?.stageConfigurations || []
      const emptyStageConfigs = stages.map((s: any) => ({
        stageConfigurationId: s.stageId || s.stageCode,
        fieldConfigurationIds: [],
      }))

      const result = await workflowApi.saveViewSettings({
        title,
        isPublic,
        isTitleAndVisibilityUpdate: false,
        isUserViewStateUpdate: false,
        updateFilters: false,
        stageConfigurations: emptyStageConfigs,
        stageFilters: [],
        globalFilters: [],
        visibleInApplications,
        authorizedClientList: authorizedClients,
      })

      if (!result?.id && !result?.viewSettingsId) throw new Error('Failed to create view')
      const viewId = result.id || result.viewSettingsId

      await refreshViews()

      // Load the new view using the same loadView helper
      // This fetches both config (for metadata) and raw view (for actual field IDs)
      // Since we created with empty field IDs, loadView will correctly show empty columns
      const clients = authorizedClients || []
      const newView: SavedView = {
        viewSettingsId: viewId,
        title,
        isPublic,
        isClientFacing,
        isDefault: false,
        authorizedClientList: clients.map(c => ({ entityId: c.entityId, name: c.name })),
      }
      await loadView(newView)
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err instanceof Error ? err.message : 'Failed to create view' })
      // Go back to views on error
      dispatch({ type: 'SET_STEP', payload: 'views' })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [dispatch, workflowApi, refreshViews, loadView, configuration])

  const handleAddClientOrg = (org: ClientOrg) => {
    const newClient: AuthorizedClient = { entityId: org.entityId, name: org.displayName }
    dispatch({
      type: 'UPDATE_VIEW_META',
      payload: { authorizedClients: [...state.draftAuthorizedClients, newClient] }
    })
    setClientSearchQuery('')
    setShowClientResults(false)
    lastClientSearchRef.current = ''
  }

  const handleRemoveClientOrg = (entityId: number) => {
    dispatch({
      type: 'UPDATE_VIEW_META',
      payload: { authorizedClients: state.draftAuthorizedClients.filter(c => c.entityId !== entityId) }
    })
  }

  const canEditView = (view: SavedView) => {
    if (view.isDefault) return hasModifyDefaultView
    if (view.isClientFacing) return hasManageClientViews
    if (view.isPublic) return hasModifyPublicViews
    return true
  }

  const canDeleteView = (view: SavedView) => {
    if (view.isDefault) return false
    return canEditView(view)
  }

  const isSelected = !!state.selectedView
  const canEditSelectedView = isSelected && state.selectedView ? canEditView(state.selectedView) : false
  const showClientSearch = canEditSelectedView && state.draftIsClientFacing && !state.selectedView?.isDefault && hasManageClientViews

  return (
    <div className="flex h-full">
      {/* ── Left Sidebar: Filters ── */}
      <div className="w-44 border-r border-gray-200 bg-[#FAFAFF] flex-shrink-0 p-4 flex flex-col">
        <div className="flex-1">
          <Label className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-3 block">
            {t('vmFilterType' as any) || 'Show'}
          </Label>
          <div className="space-y-1">
            {([
              { id: 'all' as ViewTypeFilter, label: t('all') || 'All', icon: <Globe size={14} /> },
              { id: 'internal' as ViewTypeFilter, label: 'Internal', icon: <Monitor size={14} /> },
              ...(hasManageClientViews ? [{ id: 'client' as ViewTypeFilter, label: 'Client', icon: <Lock size={14} /> }] : []),
            ]).map(opt => (
              <button
                key={opt.id}
                onClick={() => setViewTypeFilter(opt.id)}
                className={`
                  w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${viewTypeFilter === opt.id
                    ? 'bg-white text-[#0F214B] shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                  }
                `}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* ── Center: Search + Category Tabs + View List ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Search bar */}
        <div className="px-6 pt-4 pb-2 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('searchViews') || 'Search views...'}
              className="pl-9 pr-8 h-9 text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Category tabs + Create button */}
        <div className="flex items-center justify-between px-6 pb-0 flex-shrink-0">
          <div className="flex items-center gap-1">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border border-b-0 leading-[2rem]
                  ${activeCategory === cat.id
                    ? 'bg-white text-[#0F214B] border-gray-200'
                    : 'text-gray-400 hover:text-gray-600 border-transparent hover:bg-gray-50'
                  }
                `}
              >
                {cat.label}
                <span className="ml-1.5 text-xs text-gray-400">({cat.views.length})</span>
              </button>
            ))}
          </div>
          {hasManageViews && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
              onClick={() => setIsCreateModalOpen(true)}
            >
              <Plus size={16} />
              {t('createView' as any) || 'Create View'}
            </Button>
          )}
        </div>

        {/* View list */}
        <div className="flex-1 overflow-y-auto px-6 pt-2 pb-6 border-t border-gray-200 bg-white">
          {state.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
              <span className="flex-1">
                {/* Strip "API request failed: <METHOD> <URL> - " prefix so users see only the real message */}
                {state.error.replace(/^API request failed:\s+\S+\s+\S+\s+-\s+/, '')}
              </span>
              <button
                type="button"
                aria-label={t('dismissError')}
                onClick={() => dispatch({ type: 'SET_ERROR', payload: null })}
                className="text-red-500 hover:text-red-700 flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {activeViews.length === 0 ? (
            <div className="text-sm text-gray-400 py-12 text-center">
              {t('vmNoViews' as any) || 'No views found'}
            </div>
          ) : (
            <div className="mt-2 space-y-0.5">
              {activeViews.map(view => {
                const isSelectedView = state.selectedView?.viewSettingsId === view.viewSettingsId
                return (
                  <div
                    key={view.viewSettingsId}
                    className={`
                      flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-all
                      ${isSelectedView
                        ? 'bg-blue-50 border border-blue-200 shadow-sm'
                        : 'hover:bg-gray-50 border border-transparent'
                      }
                    `}
                    onClick={() => handleSelectView(view)}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`text-sm font-medium truncate ${isSelectedView ? 'text-blue-700' : 'text-gray-800'}`}>
                        {view.title}
                      </span>
                      {/* Only show DEFAULT badge outside the Default tab */}
                      {view.isDefault && activeCategory !== 'default' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium uppercase flex-shrink-0">
                          Default
                        </span>
                      )}
                      {view.isClientFacing && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded font-medium uppercase flex-shrink-0">
                          Client
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <button
                        className="p-1.5 rounded-md border border-transparent hover:border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-all"
                        onClick={() => handleToggleVisibility(view)}
                        title={view.isVisible !== false ? 'Hide' : 'Show'}
                      >
                        {view.isVisible !== false ? <Eye size={15} /> : <EyeOff size={15} />}
                      </button>
                      {canEditView(view) && (
                        <button
                          className="p-1.5 rounded-md border border-transparent hover:border-blue-200 hover:bg-blue-50 text-gray-500 hover:text-blue-600 transition-all"
                          onClick={() => handleEditView(view)}
                          title="Edit view"
                        >
                          <PenSquare size={15} />
                        </button>
                      )}
                      {hasManageViews && (
                        <button
                          className="p-1.5 rounded-md border border-transparent hover:border-purple-200 hover:bg-purple-50 text-gray-500 hover:text-purple-600 transition-all"
                          onClick={() => handleCopyView(view)}
                          title="Copy view"
                        >
                          <Copy size={15} />
                        </button>
                      )}
                      {canDeleteView(view) && (
                        <button
                          className="p-1.5 rounded-md border border-transparent hover:border-red-200 hover:bg-red-50 text-gray-500 hover:text-red-500 transition-all"
                          onClick={() => setDeleteTarget(view)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Right Side Panel: View Properties (only when user can edit) ── */}
      <div className="w-96 border-l border-gray-200 bg-gradient-to-b from-[#F4F3FF] to-[#FAFAFF] flex-shrink-0 overflow-y-auto">
        {isSelected && canEditSelectedView && state.selectedView ? (
          <div className="p-6">
            <h3 className="text-base font-bold text-[#0F214B] mb-6">
              {t('vmViewProperties' as any) || 'View Properties'}
            </h3>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('title') || 'Title'}</Label>
                <Input
                  value={state.draftViewTitle}
                  onChange={(e) => dispatch({ type: 'UPDATE_VIEW_META', payload: { title: e.target.value } })}
                  disabled={state.selectedView.isDefault}
                  className="h-10 text-sm"
                />
              </div>

              {/* Visibility */}
              {!state.selectedView.isDefault && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('visibility') || 'Visibility'}</Label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => dispatch({ type: 'UPDATE_VIEW_META', payload: { isPublic: false } })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        !state.draftIsPublic
                          ? 'bg-[#0F214B] text-white border-[#0F214B]'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Lock size={14} />
                      {t('private') || 'Private'}
                    </button>
                    <button
                      onClick={() => dispatch({ type: 'UPDATE_VIEW_META', payload: { isPublic: true } })}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        state.draftIsPublic
                          ? 'bg-[#0F214B] text-white border-[#0F214B]'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <Globe size={14} />
                      {t('public') || 'Public'}
                    </button>
                  </div>
                </div>
              )}

              {/* Client Facing */}
              {hasManageClientViews && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('clientFacing') || 'Client Facing'}</Label>
                  <button
                    onClick={() => {
                      // Warn if turning off client-facing with existing authorized clients
                      if (state.draftIsClientFacing && state.draftAuthorizedClients.length > 0) {
                        setShowClientFacingWarning(true)
                      } else {
                        dispatch({ type: 'UPDATE_VIEW_META', payload: { isClientFacing: !state.draftIsClientFacing } })
                      }
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      state.draftIsClientFacing
                        ? 'bg-amber-50 text-amber-700 border-amber-300'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {state.draftIsClientFacing ? 'Client Enabled' : 'Internal Only'}
                  </button>
                </div>
              )}

              {/* Allow Data Export — per-view opt-in for the workflow-hub
                  Excel export feature. Only shown when the operator has
                  ExportData; the grid Export button additionally AND-gates
                  on this same flag, so toggling here is the canonical way
                  to expose Export to end-users on a given view's column
                  set (e.g. PII-safe vs full Default view). Fanned out to
                  Requests-group siblings on save via the BE so toggling
                  here on ClientRequest reflects on VmsRequest/VmsEmail/
                  AtsPlacement automatically. */}
              {hasExportData && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('allowDataExport' as any) || 'Allow Data Export'}
                  </Label>
                  <button
                    onClick={() => dispatch({
                      type: 'UPDATE_VIEW_META',
                      payload: { allowDataExport: !state.draftAllowDataExport },
                    })}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                      state.draftAllowDataExport
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {state.draftAllowDataExport
                      ? (t('vmExportEnabled' as any) || 'Export Enabled')
                      : (t('vmExportDisabled' as any) || 'Export Disabled')}
                  </button>
                  <p className="text-xs text-gray-500 mt-1.5">
                    {t('vmAllowDataExportHelp' as any)
                      || 'When on, users with the Export Data permission can export this view to Excel.'}
                  </p>
                </div>
              )}

              {/* Client Organization Search */}
              {showClientSearch && (
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    {t('vmClientOrgs' as any) || 'Client Organizations'}
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Leave empty to make visible to all clients. Or select specific clients:
                  </p>

                  {/* Selected orgs as chips */}
                  {state.draftAuthorizedClients.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {state.draftAuthorizedClients.map(client => (
                        <span
                          key={client.entityId}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200"
                        >
                          {client.name || `Org #${client.entityId}`}
                          <button
                            onClick={() => handleRemoveClientOrg(client.entityId)}
                            className="hover:text-red-500 ml-0.5"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Search input */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      placeholder="Search client organizations..."
                      className="pl-8 h-8 text-xs"
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      onFocus={() => clientSearchResults.length > 0 && setShowClientResults(true)}
                    />

                    {/* Search results dropdown */}
                    {showClientResults && clientSearchResults.length > 0 && (
                      <div className="absolute z-10 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                        {clientSearchResults.map(org => (
                          <button
                            key={org.entityId}
                            onClick={() => handleAddClientOrg(org)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <span className="font-medium text-gray-800">{org.displayName}</span>
                            {org.organizationCode && (
                              <span className="ml-2 text-gray-400">({org.organizationCode})</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {isSearchingClients && (
                      <div className="absolute z-10 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                        <span className="text-xs text-gray-400">{t('loading') || 'Loading...'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Edit Columns & Filters */}
              <div className="pt-4 border-t border-gray-200/60">
                <Button
                  variant="elliptical"
                  size="sm"
                  onClick={() => dispatch({ type: 'SET_STEP', payload: 'columns' })}
                  className="w-full flex items-center justify-center gap-2"
                >
                  <PenSquare size={14} />
                  {t('vmEditColumns' as any) || 'Edit Columns & Filters'}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full p-8">
            <p className="text-sm text-gray-400 text-center leading-relaxed">
              {isSelected && !canEditSelectedView
                ? (t('vmNoEditPermission' as any) || 'You do not have permission to edit this view')
                : (t('vmSelectViewFirst' as any) || 'Select a view to see its properties')
              }
            </p>
          </div>
        )}
      </div>

      {/* ── Delete Confirmation ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteViewConfirmation') || 'Are you sure you want to delete this view?'}</AlertDialogTitle>
            <AlertDialogDescription>{t('deleteViewWarning') || 'This view will be deleted for everyone. You cannot undo this action.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline">{t('cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction variant="elliptical" onClick={handleDeleteConfirm}>{t('delete') || 'Delete'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Client Facing Warning ── */}
      <AlertDialog open={showClientFacingWarning} onOpenChange={(open) => { if (!open) setShowClientFacingWarning(false) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove client access?</AlertDialogTitle>
            <AlertDialogDescription>
              This view has {state.draftAuthorizedClients.length} authorized client organization{state.draftAuthorizedClients.length !== 1 ? 's' : ''}. Making it internal-only will remove all client organization assignments.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="elliptical-outline">
              {t('cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="elliptical"
              onClick={() => {
                dispatch({ type: 'UPDATE_VIEW_META', payload: { isClientFacing: false, authorizedClients: [] } })
                setShowClientFacingWarning(false)
              }}
            >
              Remove & continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Create View Modal ── */}
      <SaveViewModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateView}
        initialTitle=""
        initialIsPublic={false}
        initialIsClientFacing={false}
        initialAuthorizedClients={[]}
        canManageClientViews={hasManageClientViews}
        isSaveAs={false}
        isEdit={false}
        isCreateNew={true}
      />
    </div>
  )
}
